/**
 * Revision history for a scenario.
 *
 * The API has kept every revision since the first commit; nothing surfaced
 * them, which made the append-only history pointless in practice. Restoring
 * commits a copy of the old document as a new revision rather than rewinding,
 * so nothing is ever lost by looking.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, type Revision } from '../api/client';

export interface RevisionHistoryProps {
  scenarioId: string;
  /** Bumped by the parent after a commit, to refresh the list. */
  refreshToken: number;
  currentRevision: number;
  dirty: boolean;
  onRestored: () => void;
  onError: (message: string) => void;
}

export function RevisionHistory({
  scenarioId,
  refreshToken,
  currentRevision,
  dirty,
  onRestored,
  onError,
}: RevisionHistoryProps): React.ReactElement {
  const [revisions, setRevisions] = useState<Revision[] | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRevisions(await api.listRevisions(scenarioId));
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not load the history');
    }
    // onError is a parent callback; depending on it would reload the list on
    // every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const restore = async (seq: number) => {
    if (dirty) {
      const ok = window.confirm(
        'You have unsaved edits. Restoring will load the older revision and lose them. Continue?',
      );
      if (!ok) return;
    }

    setBusy(true);
    try {
      await api.restoreRevision(scenarioId, seq);
      onRestored();
    } catch (cause) {
      onError(cause instanceof Error ? cause.message : 'Could not restore that revision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <h2>History</h2>
      {revisions === null ? (
        <p className="muted small">Loading…</p>
      ) : revisions.length === 0 ? (
        <p className="muted small">No revisions yet.</p>
      ) : (
        <ul className="revisions">
          {revisions.map((revision) => (
            <li key={revision.seq} className={revision.seq === currentRevision ? 'current' : ''}>
              <div className="revision-line">
                <span className="revision-seq">r{revision.seq}</span>
                <span className="revision-message">
                  {revision.message || <span className="muted">no message</span>}
                </span>
                {revision.seq !== currentRevision && (
                  <button
                    className="ghost small-button"
                    disabled={busy}
                    onClick={() => void restore(revision.seq)}
                  >
                    Restore
                  </button>
                )}
              </div>
              <div className="muted small">
                {new Date(revision.createdAt).toLocaleString()}
                {revision.metrics?.totalAreaM2 !== undefined &&
                  ` · ${revision.metrics.totalAreaM2} m²`}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="muted small">
        Restoring appends a copy as a new revision — the history is never rewritten.
      </p>
    </section>
  );
}
